import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Athletes from "./pages/Athletes";
import AthleteProfile from "./pages/AthleteProfile";
import AthleteCreate from "./pages/AthleteCreate";
import AIAnalysis from "./pages/AIAnalysis";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import Teams from "./pages/Teams";
import ProgramDashboard from "./pages/ProgramDashboard";
import ProgramHome from "./pages/ProgramHome";
import Dashboard from "./pages/Dashboard";
import GameStorage from "./pages/GameStorage";
import AdminDashboard from "./pages/AdminDashboard";
import VenueManager from "./pages/VenueManager";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import Login from "./pages/Login";
import Register from "./pages/Register";
import SharedContent from "./pages/SharedContent";
import WatchParty from "./pages/WatchParty";
import PartnerWithUs from "./pages/PartnerWithUs";
import OperatorSelect from "./pages/OperatorSelect";
import OperatorConsole from "./pages/OperatorConsole";
import VodManagement from "./pages/VodManagement";
import GameWatchLive from "./pages/GameWatchLive";
import TeamProfile from "./pages/TeamProfile";
import Membership from "./pages/Membership";
import InternalRegister from "./pages/InternalRegister";
import ClipView from "./pages/ClipView";
import FAQ from "./pages/FAQ";
import About from "./pages/About";
import Contact from "./pages/Contact";
import { Privacy, Terms } from "./pages/LegalAliases";
import ArticlesIndex from "./pages/ArticlesIndex";
import ArticleDetail from "./pages/ArticleDetail";
import GameOverlay from "./pages/GameOverlay";
import Legal from "./pages/Legal";
import Account from "./pages/Account";
import ProgramTeams from "./pages/ProgramTeams";
import TeamHQ from "./pages/TeamHQ";
import ProgramMedia from "./pages/ProgramMedia";
import ProgramAdmin from "./pages/ProgramAdmin";
import GameHubWatch from "./pages/GameHubWatch";
import GameDayMode from "./pages/GameDayMode";
import SetupWizard from "./pages/SetupWizard";
import DemoPage from "./pages/DemoPage";
import { Toaster } from "./components/ui/sonner";
import CookieConsent from "./components/CookieConsent";

// Toggle this to false when ready to go live — restores original homepage
// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Layout wrapper that hides navbar/footer for operator routes
function Layout({ children }) {
  const location = useLocation();
  
  // Routes that should hide BOTH navbar and footer (fullscreen/operator modes)
  const hideNavAndFooter = 
    location.pathname.startsWith('/operator') || 
    location.pathname.startsWith('/vod') || 
    location.pathname.startsWith('/overlay/') ||
    location.pathname.startsWith('/game-day/') ||
    /^\/[^/]+\/watch\//.test(location.pathname);
  
  // Routes that have their own custom header but should still show footer
  const customHeaderRoutes = location.pathname.startsWith('/game-live/') || 
    location.pathname.startsWith('/clip/');
  
  return (
    <>
      {!hideNavAndFooter && !customHeaderRoutes && <Navbar />}
      {children}
      {!hideNavAndFooter && <Footer />}
    </>
  );
}

// Payment gate: redirects pending_payment CUSTOMER users to /membership
// Admins, super_admins, and operators are NEVER blocked
function PaymentGate({ children }) {
  const { user, isAuthenticated, isPendingPayment } = useAuth();
  const location = useLocation();
  
  if (isAuthenticated() && isPendingPayment()) {
    // Admins/operators/super_admins bypass payment gate entirely
    const role = user?.role || '';
    if (['admin', 'super_admin', 'operator'].includes(role)) {
      return children;
    }
    
    // Event pass holders can browse the entire site freely.
    // They are ONLY blocked from:
    // 1. Creating an athlete profile
    // 2. Watching games outside their purchased event (handled by game access API)
    const blockedPaths = ['/athlete/create'];
    const isBlocked = blockedPaths.some(p => location.pathname === p);
    if (isBlocked) {
      return <Navigate to="/membership?signup=true" replace />;
    }
  }
  
  return children;
}

// Admin/Staff-only route guard: prevents customer roles from accessing admin pages
function StaffOnly({ children }) {
  const { user, isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" /></div>;
  }
  
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  
  const staffRoles = ['admin', 'super_admin', 'operator', 'team_admin', 'scorekeeper'];
  if (!staffRoles.includes(user?.role)) {
    return <Navigate to="/events" replace />;
  }
  
  return children;
}

function ScorekeeperGuard({ children }) {
  const { user, isAuthenticated } = useAuth();
  if (isAuthenticated() && user?.role === 'scorekeeper') {
    return <Navigate to="/operator" replace />;
  }
  return children;
}

// Redirect already-logged-in users away from login/register pages
function LoginRedirect({ children }) {
  const { user, isAuthenticated, loading } = useAuth();
  
  if (loading) return children;
  
  if (isAuthenticated()) {
    const role = user?.role || '';
    if (['admin', 'super_admin'].includes(role)) {
      return <Navigate to="/admin" replace />;
    } else if (role === 'operator') {
      return <Navigate to="/operator" replace />;
    } else if (role === 'scorekeeper') {
      return <Navigate to="/operator" replace />;
    } else {
      return <Navigate to="/events" replace />;
    }
  }
  
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Layout>
            <PaymentGate>
            <Routes>
              <Route path="/" element={<ScorekeeperGuard><Home /></ScorekeeperGuard>} />
              <Route path="/home" element={<ScorekeeperGuard><Home /></ScorekeeperGuard>} />
              <Route path="/browse" element={<Navigate to="/events?active_tab=today" replace />} />
              <Route path="/athletes" element={<ScorekeeperGuard><Athletes /></ScorekeeperGuard>} />
              <Route path="/athlete/create" element={<ScorekeeperGuard><AthleteCreate /></ScorekeeperGuard>} />
              <Route path="/athlete/:id" element={<ScorekeeperGuard><AthleteProfile /></ScorekeeperGuard>} />
              <Route path="/analysis" element={<ScorekeeperGuard><AIAnalysis /></ScorekeeperGuard>} />
              <Route path="/events" element={<ScorekeeperGuard><Events /></ScorekeeperGuard>} />
              <Route path="/event/:id" element={<ScorekeeperGuard><EventDetail /></ScorekeeperGuard>} />
              <Route path="/teams" element={<ScorekeeperGuard><Teams /></ScorekeeperGuard>} />
              <Route path="/teams/:slug" element={<ScorekeeperGuard><ProgramDashboard /></ScorekeeperGuard>} />
              <Route path="/:slug/dashboard" element={<ScorekeeperGuard><ProgramHome /></ScorekeeperGuard>} />
              <Route path="/:slug/teams" element={<ScorekeeperGuard><ProgramTeams /></ScorekeeperGuard>} />
              <Route path="/:slug/teams/:teamSlug" element={<ScorekeeperGuard><TeamHQ /></ScorekeeperGuard>} />
              <Route path="/:slug/media" element={<ScorekeeperGuard><ProgramMedia /></ScorekeeperGuard>} />
              <Route path="/:slug/admin" element={<ScorekeeperGuard><ProgramAdmin /></ScorekeeperGuard>} />
              <Route path="/:slug/watch/:sessionId" element={<ScorekeeperGuard><GameHubWatch /></ScorekeeperGuard>} />
              {/* Staff-only routes */}
              <Route path="/dashboard" element={<StaffOnly><ScorekeeperGuard><Dashboard /></ScorekeeperGuard></StaffOnly>} />
              <Route path="/storage" element={<StaffOnly><ScorekeeperGuard><GameStorage /></ScorekeeperGuard></StaffOnly>} />
              <Route path="/admin" element={<StaffOnly><ScorekeeperGuard><AdminDashboard /></ScorekeeperGuard></StaffOnly>} />
              <Route path="/venues" element={<StaffOnly><ScorekeeperGuard><VenueManager /></ScorekeeperGuard></StaffOnly>} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/login" element={<LoginRedirect><Login /></LoginRedirect>} />
              <Route path="/register" element={<LoginRedirect><Register /></LoginRedirect>} />
              <Route path="/share/:type/:id" element={<SharedContent />} />
              <Route path="/watch-party/:teamId" element={<ScorekeeperGuard><WatchParty /></ScorekeeperGuard>} />
              <Route path="/partner" element={<ScorekeeperGuard><PartnerWithUs /></ScorekeeperGuard>} />
              <Route path="/operator" element={<StaffOnly><OperatorSelect /></StaffOnly>} />
              <Route path="/operator/game/:gameId" element={<StaffOnly><OperatorConsole /></StaffOnly>} />
              <Route path="/vod" element={<StaffOnly><ScorekeeperGuard><VodManagement /></ScorekeeperGuard></StaffOnly>} />
              <Route path="/game-live/:gameId" element={<GameWatchLive />} />
              <Route path="/team/:teamName" element={<ScorekeeperGuard><TeamProfile /></ScorekeeperGuard>} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/articles" element={<ArticlesIndex />} />
              <Route path="/articles/:slug" element={<ArticleDetail />} />
              <Route path="/membership" element={<ScorekeeperGuard><Membership /></ScorekeeperGuard>} />
              <Route path="/membership/success" element={<ScorekeeperGuard><Membership /></ScorekeeperGuard>} />
              <Route path="/internal/register/:roleType" element={<InternalRegister />} />
              <Route path="/clip/:clipId" element={<ClipView />} />
              <Route path="/overlay/:gameId" element={<GameOverlay />} />
              <Route path="/game-day/:eventId" element={<StaffOnly><GameDayMode /></StaffOnly>} />
              <Route path="/setup-wizard" element={<StaffOnly><SetupWizard /></StaffOnly>} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/account" element={<ScorekeeperGuard><Account /></ScorekeeperGuard>} />
              <Route path="/demo" element={<DemoPage />} />
            </Routes>
            </PaymentGate>
          </Layout>
          <Toaster />
          <CookieConsent />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
